// REVIEW POINT 7: This page is wrapped in <ProtectedRoute requireAdmin>
// in src/App.tsx. Non-admins hit the existing "Access Denied" card.
// The page itself is functional only — no styling beyond shadcn defaults.

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface UniverseRow {
  eligible: boolean;
  excluded_reason: string | null;
}

interface PopulationRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  provider_status: any;
  universe_stats: any;
  failure_report: any;
  triggered_by: string | null;
}

interface ScoreRow {
  token_id: string;
  total_score: number | null;
  setup_score: number | null;
  value_score: number | null;
  liquidity_score: number | null;
  disqualified: boolean;
  disqualified_reasons: string[];
  raw_subscores: any;
}

export default function GemDebug() {
  const [universe, setUniverse] = useState<UniverseRow[]>([]);
  const [scores, setScores] = useState<ScoreRow[]>([]);
  const [runs, setRuns] = useState<PopulationRun[]>([]);
  const [running, setRunning] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<any>(null);

  async function load() {
    const { data: uni } = await supabase
      .from("gem_universe" as any)
      .select("eligible, excluded_reason");
    setUniverse((uni as any) ?? []);

    const { data: sc } = await supabase
      .from("gem_scores" as any)
      .select("token_id, total_score, setup_score, value_score, liquidity_score, disqualified, disqualified_reasons, raw_subscores")
      .order("total_score", { ascending: false })
      .limit(100);
    setScores((sc as any) ?? []);

    const { data: rs } = await supabase
      .from("gem_population_runs" as any)
      .select("id, started_at, finished_at, status, provider_status, universe_stats, failure_report, triggered_by")
      .order("started_at", { ascending: false })
      .limit(5);
    setRuns((rs as any) ?? []);
  }

  useEffect(() => { load(); }, []);

  async function trigger(fn: string, label: string) {
    setRunning(fn);
    setLastResult(null);
    try {
      const { data, error } = await supabase.functions.invoke(fn);
      if (error) {
        toast.error(`${label} failed: ${error.message}`);
        setLastResult({ error: error.message });
      } else {
        toast.success(`${label} complete`);
        setLastResult(data);
      }
      await load();
    } catch (e: any) {
      toast.error(`${label} threw: ${e?.message ?? e}`);
      setLastResult({ error: String(e) });
    } finally {
      setRunning(null);
    }
  }

  const eligibleCount = universe.filter(u => u.eligible).length;
  const exclusions = universe.reduce<Record<string, number>>((acc, u) => {
    if (!u.eligible && u.excluded_reason) {
      acc[u.excluded_reason] = (acc[u.excluded_reason] ?? 0) + 1;
    }
    return acc;
  }, {});

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">GEM Score — Phase 1 Debug</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manual triggers + raw scoring inspection. Admin-only.
        </p>
      </div>

      <Card className="border-destructive/40 bg-destructive/5">
        <CardContent className="pt-6 text-sm space-y-2">
          <div className="font-semibold text-destructive">
            ⚠ Not production. Do not surface these scores anywhere user-facing.
          </div>
          <div className="text-muted-foreground">
            The current model is an interim 3-factor screen (setup + value + liquidity).
            The public GEM promise requires the full 6-factor model — fundamentals, catalyst,
            and sentiment scorers must ship first. Until then, nothing from this page
            belongs on the homepage, in nav, or in any component visible to end users.
          </div>
        </CardContent>
      </Card>


      <div className="flex flex-wrap gap-2">
        <Button
          disabled={!!running}
          onClick={() => trigger("gem-populate-universe", "Universe population")}
        >
          {running === "gem-populate-universe" ? "Running…" : "Populate universe"}
        </Button>
        <Button
          disabled={!!running}
          variant="secondary"
          onClick={() => trigger("gem-score-all", "Score all eligible")}
        >
          {running === "gem-score-all" ? "Running…" : "Score all eligible"}
        </Button>
      </div>

      {lastResult && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Last invocation result</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-xs overflow-x-auto bg-muted p-3 rounded max-h-80">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Universe Health — last 5 runs</CardTitle></CardHeader>
        <CardContent>
          {runs.length === 0 && (
            <div className="text-sm text-muted-foreground">No population runs recorded yet.</div>
          )}
          <div className="space-y-3">
            {runs.map(r => {
              const ps = r.provider_status ?? {};
              const stats = r.universe_stats ?? {};
              const exc = (stats.excluded ?? {}) as Record<string, number>;
              const topExc = Object.entries(exc).sort((a, b) => b[1] - a[1]).slice(0, 5);
              const statusVariant: "default" | "destructive" | "secondary" =
                r.status === "ok" ? "default" : r.status === "failed" ? "destructive" : "secondary";
              return (
                <div key={r.id} className="border rounded p-3 text-xs space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-mono text-muted-foreground">
                      {new Date(r.started_at).toLocaleString()}
                      {r.finished_at && ` → ${new Date(r.finished_at).toLocaleTimeString()}`}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={statusVariant}>{r.status}</Badge>
                      {r.triggered_by && <span className="text-muted-foreground">{r.triggered_by}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(ps).map(([prov, info]: [string, any]) => {
                      const v: "default" | "destructive" | "secondary" | "outline" =
                        info.status === "ok" ? "default" :
                        info.status === "failed" ? "destructive" :
                        info.status === "simulated_failure" ? "destructive" : "outline";
                      return (
                        <Badge key={prov} variant={v} className="text-[10px] font-mono">
                          {prov}: {info.status}{info.count ? ` (${info.count})` : ""}
                          {info.reason ? ` — ${String(info.reason).slice(0, 60)}` : ""}
                        </Badge>
                      );
                    })}
                  </div>
                  {(stats.cmc_scanned != null || stats.eligible != null) && (
                    <div className="font-mono">
                      scanned: <strong>{stats.cmc_scanned ?? "—"}</strong>{" · "}
                      with_au: <strong>{stats.with_au_listing ?? "—"}</strong>{" · "}
                      eligible: <strong className="text-green-600">{stats.eligible ?? "—"}</strong>
                    </div>
                  )}
                  {topExc.length > 0 && (
                    <div>
                      <div className="text-muted-foreground">Top exclusions:</div>
                      <ul className="space-y-0.5">
                        {topExc.map(([reason, count]) => (
                          <li key={reason} className="font-mono">
                            {reason}: <strong>{count}</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.failure_report && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-muted-foreground">failure_report</summary>
                      <pre className="bg-muted p-2 rounded mt-1 overflow-x-auto max-h-60">
                        {JSON.stringify(r.failure_report, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Universe summary</CardTitle></CardHeader>
        <CardContent>
          <div className="text-sm space-y-1">
            <div>Total tokens: <strong>{universe.length}</strong></div>
            <div>Eligible: <strong className="text-green-600">{eligibleCount}</strong></div>
            <div className="mt-3 font-semibold">Exclusion breakdown:</div>
            {Object.keys(exclusions).length === 0 && (
              <div className="text-muted-foreground">No exclusions yet — run universe population.</div>
            )}
            <ul className="space-y-0.5">
              {Object.entries(exclusions)
                .sort((a, b) => b[1] - a[1])
                .map(([reason, count]) => (
                  <li key={reason} className="font-mono text-xs">
                    <span className="text-muted-foreground">{reason}:</span>{" "}
                    <strong>{count}</strong>
                  </li>
                ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Latest scores (top 100 by total)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {scores.length === 0 && (
            <div className="text-sm text-muted-foreground">No scores yet — run "Score all eligible".</div>
          )}
          {scores.length > 0 && (
            <table className="text-xs w-full">
              <thead className="border-b">
                <tr className="text-left">
                  <th className="py-1 pr-3">Token</th>
                  <th className="py-1 pr-3">Total</th>
                  <th className="py-1 pr-3">Setup</th>
                  <th className="py-1 pr-3">Value</th>
                  <th className="py-1 pr-3">Liq</th>
                  <th className="py-1 pr-3">DQ</th>
                  <th className="py-1 pr-3">Disqualifier reasons</th>
                </tr>
              </thead>
              <tbody>
                {scores.map(s => (
                  <tr key={s.token_id} className={`border-b ${s.disqualified ? "opacity-50" : ""}`}>
                    <td className="py-1 pr-3 font-mono">{s.token_id}</td>
                    <td className="py-1 pr-3 font-bold">{s.total_score?.toFixed(1) ?? "—"}</td>
                    <td className="py-1 pr-3">{s.setup_score?.toFixed(0) ?? "—"}</td>
                    <td className="py-1 pr-3">{s.value_score?.toFixed(0) ?? "—"}</td>
                    <td className="py-1 pr-3">{s.liquidity_score?.toFixed(0) ?? "—"}</td>
                    <td className="py-1 pr-3">
                      {s.disqualified
                        ? <Badge variant="destructive" className="text-[10px]">DQ</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-1 pr-3 font-mono text-[10px]">
                      {(s.disqualified_reasons ?? []).join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
